import React, { useEffect } from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as approvalInitialState } from '~redux/slices/approval';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import * as Styles from './_stockOutApprovalSearchAndFilterBar.module.scss';
import { BranchStatusEnum, MetalTypeEnum } from '~utils/enums';
import dayjs from 'dayjs';
import { TGetStockOutsListingFilters } from '~types/feat/stockOut';
import { GoldSilverInventorySelectedTab } from '~types/redux/state';
import { TGetPendingStockOutListingFilters } from '~types/feat/approval';

const StockOutSearchAndFilterBar = () => {
    const dispatch = useAppDispatch();

    const filters = useAppSelector(state => state.approval.pendingStockOutListing.filters);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);
    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active);

    const initialFilters = approvalInitialState.pendingStockOutListing.filters;

    const setFilters = (inputFilters: TGetPendingStockOutListingFilters) => dispatch(combinedActions.approvalSetPendingStockOutListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.approvalGetPendingStockOutListingAttempt());
    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
            branchCode: !allBranchAccess ? branchCode : '',
            branchName: !allBranchAccess ? (branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '') : '',
        });
        handleSearchClick();
    };

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            setFilters({
                ...filters,
                branchCode,
                branchName: branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '',
            });
        }
    }, [branchesList]);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];
        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    useEffect(() => {
        return () => {
            // clear filters on unmount
            handleCancelClick();
        };
    }, []);

    const fields: TSearchAndFilterBarProps['fields'] = [

        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom || '',
                setDate: (date: string) => {
                    if (filters.dateTo && date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
                maxDate: dayjs().toISOString(),
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo || '',
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Gold',
                        value: MetalTypeEnum.Gold,
                    },
                    {
                        label: 'Silver',
                        value: MetalTypeEnum.Silver,
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, metalType: event.target.value }),
                optionSelected: !!MetalTypeEnum[filters.metalType as keyof typeof MetalTypeEnum],
                noOptionsMessage: 'No metal type found',
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                    branchCode: branchOptions.find((branch) => branch.branchName === event.target.value)?.branchCode || '',
                }),
                optionSelected: !!branchOptions.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branch found',
                disabled: !allBranchAccess,
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default StockOutSearchAndFilterBar;
